import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "@/api/client";

interface PackRow {
  version: number;
  description: string;
  effective: string;
  entry_count: number;
  state: "pending" | "canary" | "stable";
  uploaded_at?: string;
  promoted_at?: string;
  promoted_by?: string;
  published: boolean;
}

interface PacksResponse {
  items: PackRow[];
  published_to: number;
  max_known_ver: number;
}

function StateBadge({ state }: { state: string }) {
  const cls =
    state === "stable"
      ? "bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300"
      : state === "canary"
      ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
      : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300";
  return (
    <span className={`rounded ${cls} px-2 py-0.5 text-xs font-semibold uppercase tracking-wide`}>
      {state}
    </span>
  );
}

export function PacksPage() {
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const q = useQuery<PacksResponse>({
    queryKey: ["packs"],
    queryFn: () => api<PacksResponse>("/api/v1/packs"),
    staleTime: 10_000,
  });
  const promote = useMutation({
    mutationFn: async ({ version, state }: { version: number; state: "canary" | "stable" }) =>
      api(`/api/v1/admin/packs/${version}/promote`, {
        method: "POST",
        body: { state },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["packs"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (q.isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 401) {
      return (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Sign in as an operator to view rule packs.
        </div>
      );
    }
    return (
      <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        Could not load packs: {(q.error as Error).message}
      </div>
    );
  }
  const data = q.data!;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Rule Packs</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Signed by the SA with HMAC-SHA256. Versions ≤ {data.published_to} are
          live to enrolled kits; higher versions are staged but not yet rolled.
          Promotion canary → stable is a one-way operator action.
        </p>
      </header>
      {error && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300 mb-3">
          {error}
        </div>
      )}
      <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-700 text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Version</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Effective</th>
              <th className="px-3 py-2">Entries</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Published?</th>
              <th className="px-3 py-2">Promoted by</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">No packs yet.</td></tr>
            )}
            {data.items.map((p) => (
              <tr key={p.version} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                <td className="px-3 py-2 font-mono tabular-nums">v{p.version}</td>
                <td className="px-3 py-2">{p.description}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.effective}</td>
                <td className="px-3 py-2 tabular-nums">{p.entry_count}</td>
                <td className="px-3 py-2"><StateBadge state={p.state} /></td>
                <td className="px-3 py-2">{p.published ? "✓" : "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">{p.promoted_by ?? "—"}</td>
                <td className="px-3 py-2 space-x-1">
                  {p.state !== "canary" && (
                    <button
                      className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => promote.mutate({ version: p.version, state: "canary" })}
                    >→ canary</button>
                  )}
                  {p.state !== "stable" && (
                    <button
                      className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => {
                        if (confirm(`Promote v${p.version} to stable? This rolls the pack out fleet-wide.`)) {
                          promote.mutate({ version: p.version, state: "stable" });
                        }
                      }}
                    >→ stable</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
